import { NotificationProps } from '@mantine/core';
import { Badge, Icon, variants } from '@uag/react-core';
import { AiPlausibilityRiskLevel } from '@uag/welcome-shared/src/api/models';
import { ComponentProps } from 'react';

import { TranslationKeys } from 'i18n';

export type KnownRiskLevel = 'low' | 'medium' | 'high';

type RiskLevelConfig = {
    gaugeClassName: string;
    notificationVariant: NotificationProps['variant'];
    badge: Pick<ComponentProps<typeof Badge>, 'variant' | 'icon'>;
    findingIcon: Pick<ComponentProps<typeof Icon>, 'icon' | 'className'>;
    labelKey: TranslationKeys;
    hintKey: TranslationKeys;
};

export const riskLevelConfig = {
    high: {
        gaugeClassName: 'stroke-background-alert',
        notificationVariant: variants.notification.inlineNotificationAlert,
        badge: { variant: variants.badge.alert, icon: 'error' },
        findingIcon: { icon: 'warning', className: 'text-icon-alert' },
        labelKey: 'aiPlausibilityHighRisk',
        hintKey: 'aiPlausibilityHintHighRisk',
    },
    medium: {
        gaugeClassName: 'stroke-background-warning',
        notificationVariant: variants.notification.inlineNotificationWarning,
        badge: { variant: variants.badge.warning, icon: 'warning' },
        findingIcon: { icon: 'warning', className: 'text-icon-warning' },
        labelKey: 'aiPlausibilityMediumRisk',
        hintKey: 'aiPlausibilityHintMediumRisk',
    },
    low: {
        gaugeClassName: 'stroke-background-success',
        notificationVariant: variants.notification.inlineNotificationSuccess,
        badge: { variant: variants.badge.success, icon: 'check_circle' },
        findingIcon: { icon: 'info', className: 'text-icon-information' },
        labelKey: 'aiPlausibilityLowRisk',
        hintKey: 'aiPlausibilityHintLowRisk',
    },
} as const satisfies Record<KnownRiskLevel, RiskLevelConfig>;

const riskLevelByApiValue: Partial<Record<AiPlausibilityRiskLevel, KnownRiskLevel>> = {
    [AiPlausibilityRiskLevel.Low]: 'low',
    [AiPlausibilityRiskLevel.Medium]: 'medium',
    [AiPlausibilityRiskLevel.High]: 'high',
};

export const toKnownRiskLevel = (risk?: AiPlausibilityRiskLevel | null): KnownRiskLevel | undefined =>
    risk ? riskLevelByApiValue[risk] : undefined;
